import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { Panel } from './EditorFields';

const GENDER = [['female', 'Female only (e.g. Women Job Fair)'], ['male', 'Male only'], ['all', 'All genders']];

const FieldTable = ({ defs, cfg, onChange, testId }) => (
  <div className="divide-y border rounded-lg" data-testid={testId}>
    <div className="grid grid-cols-[1fr_6rem_6rem] px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50"><span>Field</span><span className="text-center">Show</span><span className="text-center">Mandatory</span></div>
    {defs.map((d) => {
      const c = cfg?.[d.key] || { visible: true, required: d.required };
      const set = (patch) => onChange({ ...cfg, [d.key]: { ...c, ...patch } });
      return (
        <div key={d.key} className="grid grid-cols-[1fr_6rem_6rem] px-3 py-2 items-center text-sm">
          <span className="flex items-center gap-2">{d.label}{d.locked && <Lock className="w-3 h-3 text-slate-400" />}<span className="text-xs text-slate-400">{d.section}</span></span>
          <span className="flex justify-center"><Switch disabled={d.locked} checked={c.visible} onCheckedChange={(v) => set({ visible: v, ...(v ? {} : { required: false }) })} data-testid={`${testId}-${d.key}-visible`} /></span>
          <span className="flex justify-center"><Switch disabled={d.locked || !c.visible} checked={c.required} onCheckedChange={(v) => set({ required: v })} data-testid={`${testId}-${d.key}-required`} /></span>
        </div>
      );
    })}
  </div>
);

export const FormSettingsTab = ({ f, set }) => {
  const [defs, setDefs] = useState({ candidate: [], employer: [] });
  useEffect(() => { api.get('/job-fairs/admin/field-definitions').then((r) => setDefs(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-5">
      <Panel title="Registration Availability">
        {[['candidate_registration_enabled', 'Job Seeker registration open'], ['employer_registration_enabled', 'Employer registration open']].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-3 text-sm">
            <span>{l}<span className="block text-xs text-slate-400">Registration also closes automatically when the fair is deactivated or completed.</span></span>
            <Switch checked={f[k] !== false} onCheckedChange={set(k)} data-testid={`editor-${k}`} />
          </label>
        ))}
      </Panel>
      <Panel title="Candidate Gender" desc="If only one gender is allowed, the form pre-fills and locks it.">
        <div className="grid sm:grid-cols-3 gap-2">
          {GENDER.map(([v, l]) => (
            <button type="button" key={v} onClick={() => set('gender_mode')(v)} data-testid={`editor-gender-${v}`}
              className={`rounded-lg border px-3 py-2.5 text-sm text-left transition-colors ${f.gender_mode === v ? 'border-teal-600 bg-teal-50 text-teal-800 font-semibold' : 'hover:bg-slate-50'}`}>{l}</button>
          ))}
        </div>
      </Panel>
      <Panel title="Job Seeker Form Fields"><FieldTable defs={defs.candidate} cfg={f.candidate_fields} onChange={set('candidate_fields')} testId="editor-candidate-field" /></Panel>
      <Panel title="Employer Form Fields"><FieldTable defs={defs.employer} cfg={f.employer_fields} onChange={set('employer_fields')} testId="editor-employer-field" /></Panel>
      <Panel title="Declarations" desc="Leave empty to use the default wording.">
        <div><Label className="text-sm">Job Seeker declaration</Label><Textarea rows={3} value={f.candidate_declaration || ''} onChange={(e) => set('candidate_declaration')(e.target.value)} data-testid="editor-candidate-declaration" /></div>
        <div><Label className="text-sm">Employer declaration</Label><Textarea rows={3} value={f.employer_declaration || ''} onChange={(e) => set('employer_declaration')(e.target.value)} data-testid="editor-employer-declaration" /></div>
      </Panel>
    </div>
  );
};
